import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import GeoResponsiveList from "../../common/Components/GeoList/GeoResponsiveList";
import StudioRowActions from "../../common/Components/GeoList/StudioRowActions";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import {
  confirmKarkhanaWorkerProfile,
  createKarkhanaWorkerProfile,
  getPendingWorkerCount,
  listKarkhanaWorkerCandidates,
  searchKarkhanaWorkers,
  syncKarkhanaWorkerUsers,
  updateKarkhanaWorkerProfile,
} from "../../services/karkhanaApi";
import { geoRecordId } from "../gi-registry/geoPath";
import "../gi-registry/gi-registry.scss";
import WorkersNav from "./workers/WorkersNav";
import { postLabel, previewCode, useWorkerSetup, workerPost } from "./workers/useWorkerSetup";
import "./workers/karkhana-workers.scss";
import WorkerKycSection from "./workers/WorkerKycSection";
import { HkpId } from "../users/UserIds";
import PageHeader, { HeaderButton } from "./ui/PageHeader";
import SearchField from "./ui/SearchField";
import { confirmDialog } from "./ui/dialog";
import { useKT } from "./i18n/kt";
import { kp } from "./karkhanaPaths";
import { useAddressParam } from "./ui/addressState";

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const emptyForm = () => ({
  name: "",
  categoryId: "",
  workerCode: "",
  address: "",
  phone: "",
  userId: "",
  payType: "",
  notes: "",
});

const PAGE = 50;

/** A user's name, with phone and HKP ID when another user has the same name. */
export const candidateLabel = (person: any, all: any[]): string => {
  const name = String(person?.name || "").trim();
  const same = all.filter((p) => String(p?.name || "").trim().toLowerCase() === name.toLowerCase()).length > 1;
  const extra = [person?.phone ? `…${String(person.phone).slice(-4)}` : "", person?.hkpId || ""].filter(Boolean);
  if (!same) return name;
  return extra.length ? `${name} (${extra.join(" · ")})` : `${name} (${String(person?.userId || "").slice(-4)})`;
};

const KarkhanaWorkersPage = () => {
  const history = useHistory();
  const role = readAuthUser()?.users?.role;
  const canWrite = canManageKarkhana(role) && role !== "SUPER_ADMIN";
  const isOrgAdmin = role === "ORGANIZATION_ADMIN";
  const { t } = useKT();
  const { categories, policy, reviewCount, loaded, reload: reloadSetup } = useWorkerSetup();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  // Search, page and "only waiting" are in the address (replaced): Back and reload return to the same list.
  const [pageParam, setPageParam] = useAddressParam("page", "1");
  const page = Math.max(0, (parseInt(pageParam, 10) || 1) - 1);
  const setPage = (p: number) => setPageParam(String(p + 1));
  const [query, setQuery] = useAddressParam("q");
  const [candidates, setCandidates] = useState<any[]>([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Workers a supervisor added that wait for the owner: counted across every page by the server.
  // null until the server answers (or if it can't): then the rows on screen are counted instead.
  const [serverWaiting, setServerWaiting] = useState<number | null>(null);
  const [waitingParam, setWaitingParam] = useAddressParam("waiting");
  const waitingOnly = waitingParam === "1";
  const setWaitingOnly = (on: boolean) => setWaitingParam(on ? "1" : "");
  const searchTimer = useRef<number | undefined>(undefined);

  const loadWaitingCount = useCallback(async () => {
    try {
      const res: any = await getPendingWorkerCount();
      setServerWaiting(res?.success ? Number(res.data?.count || 0) : null);
    } catch {
      // The count is a hint; fall back to the rows on screen, which still carry each waiting worker's chip.
      setServerWaiting(null);
    }
  }, []);

  /** Loads a page; returns how many match (null when it failed). */
  const load = useCallback(async (q: string, p: number, waiting = false): Promise<number | null> => {
    setLoading(true);
    setError("");
    try {
      const res: any = await searchKarkhanaWorkers({ q: q.trim() || undefined, offset: p, limit: PAGE, waiting });
      if (!res?.success) {
        setRows([]);
        setError(res?.message || "Unable to load workers");
        return null;
      }
      setRows(Array.isArray(res?.data?.docs) ? res.data.docs : []);
      const count = Number(res?.data?.length || 0);
      setTotal(count);
      return count;
    } catch {
      setRows([]);
      setError("Unable to load workers");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    load(query, page, waitingOnly);
    loadWaitingCount();
  }, [role, history, load, loadWaitingCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (value: string) => {
    setQuery(value);
    window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => {
      setPage(0);
      load(value, 0, waitingOnly);
    }, 250);
  };

  const goPage = (p: number) => {
    setPage(p);
    load(query, p, waitingOnly);
  };

  const showWaiting = (on: boolean) => {
    // A search typed just before must not fire later with the old filter.
    window.clearTimeout(searchTimer.current);
    setWaitingOnly(on);
    setPage(0);
    load(query, 0, on);
  };

  const loadCandidates = async () => {
    if (!canWrite || candidates.length) return;
    const res: any = await listKarkhanaWorkerCandidates();
    setCandidates(Array.isArray(res?.data) ? res.data : []);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), categoryId: categories.find((c) => c.active)?.id || "" });
    setOpen(true);
    loadCandidates();
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      categoryId: (row.categoryIds || [])[0] || "",
      workerCode: row.workerCode || "",
      address: row.address || "",
      phone: row.phone || "",
      userId: row.userId || "",
      payType: row.payType || "",
      notes: row.notes || "",
    });
    setOpen(true);
  };

  const selectedCategory = categories.find((c) => c.id === form.categoryId);
  const generatedPreview = previewCode(policy, selectedCategory?.prefix);
  const codeChanged = editing && form.workerCode.trim().toUpperCase() !== String(editing.workerCode || "");

  const persist = async () => {
    if (!form.name.trim() && !form.userId) {
      notifications({ title: "Error", message: "Enter the worker's name", type: "danger" });
      return;
    }
    if (!editing && !form.workerCode.trim() && !form.categoryId) {
      notifications({ title: "Error", message: "Choose a category so a code can be made", type: "danger" });
      return;
    }
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim() || undefined,
        workerCode: form.workerCode.trim() || undefined,
        categoryIds: form.categoryId ? [form.categoryId] : [],
        address: form.address.trim(),
        phone: form.phone.trim(),
        payType: form.payType || undefined,
        notes: form.notes.trim() || undefined,
      };
      const res: any = editing
        ? await updateKarkhanaWorkerProfile(geoRecordId(editing), body)
        : await createKarkhanaWorkerProfile({ ...body, userId: form.userId || undefined });
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not save worker", type: "danger" });
        return;
      }
      if (!editing) {
        notifications({ title: "Saved", message: `Added ${res.data?.workerCode}`, type: "success" });
      }
      setOpen(false);
      await Promise.all([load(query, page, waitingOnly), reloadSetup(), loadWaitingCount()]);
    } catch {
      notifications({ title: "Error", message: "Could not reach the server", type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row: any) => {
    const id = geoRecordId(row);
    if (!id) return;
    const res: any = await updateKarkhanaWorkerProfile(id, { active: !row.active });
    if (!res?.success) {
      notifications({ title: "Error", message: res?.message || "Could not update", type: "danger" });
      return;
    }
    await load(query, page, waitingOnly);
  };

  // A worker a supervisor added is unpaid until the studio admin confirms them.
  const [confirming, setConfirming] = useState<string | null>(null);
  const confirmWorker = async (row: any) => {
    const id = geoRecordId(row);
    if (!id || confirming) return;
    const name = row.name || row.userName || row.workerCode;
    const ok = await confirmDialog({
      title: t("Confirm {{name}}?", { name }),
      body: row.addedByName
        ? t("{{who}} added {{name}} from the floor. Once you confirm, their pay can be entered.", { who: row.addedByName, name })
        : t("A supervisor added {{name}} from the floor. Once you confirm, their pay can be entered.", { name }),
      ok: t("Confirm"),
    });
    if (!ok) return;
    setConfirming(id);
    try {
      const res: any = await confirmKarkhanaWorkerProfile(id);
      if (!res?.success) {
        notifications({ title: t("Not confirmed"), message: t(res?.message || "Could not update"), type: "danger" });
        return;
      }
      notifications({ title: t("Confirmed"), message: t("{{name}} can now be paid.", { name }), type: "success" });
      loadWaitingCount();
      if (!waitingOnly) {
        await load(query, page);
      } else {
        // The waiting list shrank: start it again from the top, and when nobody is left go back to everyone.
        setPage(0);
        const left = await load(query, 0, true);
        if (left === 0) {
          setWaitingOnly(false);
          await load(query, 0);
        }
      }
    } catch {
      notifications({ title: t("Not confirmed"), message: t("Could not reach the server"), type: "danger" });
    } finally {
      setConfirming(null);
    }
  };

  const syncUsers = async () => {
    const ok = await confirmDialog({
      title: t("Add workers to Users?"),
      body: t(
        "Every worker whose category has an HKP post and who is not in Users yet is added, or linked to the studio user with the same phone. They can't sign in."
      ),
      ok: t("Sync to Users"),
    });
    if (!ok) return;
    setSyncing(true);
    try {
      const res: any = await syncKarkhanaWorkerUsers();
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not sync workers", type: "danger" });
        return;
      }
      notifications({
        title: t("Sync to Users"),
        message: t("Added {{created}}, linked {{linked}}, not added {{skipped}}.", {
          created: res.data?.created ?? 0,
          linked: res.data?.linked ?? 0,
          skipped: res.data?.skipped ?? 0,
        }),
        type: "success",
      });
      await load(query, page, waitingOnly);
    } catch {
      notifications({ title: "Error", message: "Could not reach the server", type: "danger" });
    } finally {
      setSyncing(false);
    }
  };

  if (!canManageKarkhana(role)) {
    return null;
  }

  const waitingCount = serverWaiting ?? rows.filter((r) => r.pendingConfirmation).length;
  const catById = new Map(categories.map((c) => [c.id, c]));
  const noCategories = loaded && categories.length === 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title="Workers" back={kp.settings()}>
        {isOrgAdmin ? (
          <HeaderButton onClick={syncUsers} disabled={noCategories || syncing}>
            {syncing ? t("Syncing…") : t("Sync to Users")}
          </HeaderButton>
        ) : null}
        {canWrite ? (
          <HeaderButton primary add onClick={openCreate} disabled={noCategories}>
            Add worker
          </HeaderButton>
        ) : null}
      </PageHeader>
      <WorkersNav reviewCount={reviewCount} />

      {noCategories ? (
        <div className="kw-card">
          <h3>Set up worker categories first</h3>
          <p className="kw-lede">
            Categories give each worker a code prefix, like K for karigar or J for jodiya. New codes are made from
            them.
          </p>
          <Link className="btn btn-hastkala btn-sm" to={kp.codes()}>
            Set up categories
          </Link>
        </div>
      ) : null}

      {reviewCount > 0 ? (
        <p className="kw-lede">
          <span className="kw-chip is-review">{reviewCount} to review</span>{" "}
          Some imported workers need a decision.{" "}
          <Link to={kp.workerReview()}>Open the review list</Link>
        </p>
      ) : null}

      {isOrgAdmin && (waitingCount > 0 || waitingOnly) ? (
        <div className="kw-waiting">
          <p className="kw-lede">
            <span className="kw-chip is-review">{t("{{count}} waiting for you to confirm", { count: waitingCount })}</span>{" "}
            {t("Workers a supervisor adds get paid only after you confirm them.")}
          </p>
          <button
            type="button"
            className="kh-btn"
            aria-pressed={waitingOnly}
            onClick={() => showWaiting(!waitingOnly)}
          >
            {waitingOnly ? t("Show all workers") : t("Show only waiting")}
          </button>
        </div>
      ) : null}

      <SearchField
        value={query}
        onChange={onSearch}
        placeholder="Search by code or name"
        label="Search workers"
        autoCapitalize="characters"
        count={loading ? "Loading…" : `${total} worker${total === 1 ? "" : "s"}`}
      />

      <GeoResponsiveList
        rows={rows}
        rowKey={(ele) => geoRecordId(ele) || ele.workerCode}
        tableClassName="kw-worker-table"
        empty={
          loading
            ? "Loading workers…"
            : error || (waitingOnly ? t("Nobody is waiting") : query ? "No worker matches that search" : "No workers yet")
        }
        actions={(ele) =>
          canWrite ? (
            <>
              {isOrgAdmin && ele.pendingConfirmation ? (
                // A labelled button, not an icon: it is the one thing to do on this row.
                <button
                  type="button"
                  className="kh-btn is-primary kw-confirm"
                  disabled={!!confirming}
                  aria-busy={confirming === geoRecordId(ele)}
                  onClick={() => confirmWorker(ele)}
                >
                  {confirming === geoRecordId(ele) ? (
                    <>
                      <span className="kw-spin" aria-hidden="true" />
                      {t("Confirming…")}
                    </>
                  ) : (
                    t("Confirm")
                  )}
                </button>
              ) : null}
              <StudioRowActions
                actions={[
                  { key: "edit", label: "Edit", icon: "edit", tone: ele.pendingConfirmation && isOrgAdmin ? undefined : "primary", onClick: () => openEdit(ele) },
                  {
                    key: "time",
                    label: "Time rates",
                    icon: "edit",
                    onClick: () => history.push(kp.timeRates({ code: ele.workerCode })),
                  },
                  {
                    key: "active",
                    label: ele.active ? "Turn off" : "Turn on",
                    icon: "edit",
                    onClick: () => toggleActive(ele),
                  },
                ]}
              />
            </>
          ) : null
        }
        columns={[
          {
            key: "code",
            header: "Code",
            cell: (row) => (
              <>
                <span className="kw-code">{row.workerCode}</span>
                {row.hkpId ? <span className="kw-code kw-muted kw-sub">{row.hkpId}</span> : null}
              </>
            ),
          },
          {
            key: "name",
            header: "Name",
            cell: (row) => (
              <>
                {row.name || row.userName || "—"}
                {row.pendingConfirmation ? (
                  <span className="kw-chip is-review kw-user-chip" title={row.addedByName ? `${t("Added by")} ${row.addedByName}` : undefined}>
                    {t("Waiting for owner to confirm")}
                  </span>
                ) : null}
                {row.userId ? (
                  <span className="kw-chip is-info kw-user-chip">
                    {t("In Users")}
                    {workerPost(categories, row.categoryIds) ? ` · ${postLabel(workerPost(categories, row.categoryIds))}` : ""}
                  </span>
                ) : null}
              </>
            ),
          },
          {
            key: "category",
            header: "Category",
            cell: (row) => (
              <span className="kw-chips">
                {(row.categoryIds || []).map((id: string) => (
                  <span key={id} className="kw-chip">
                    {catById.get(id)?.name || "—"}
                  </span>
                ))}
              </span>
            ),
          },
          { key: "address", header: "Address", hideSm: true, cell: (row) => row.address || "—" },
          {
            key: "aliases",
            header: "Old codes",
            hideSm: true,
            cell: (row) =>
              (row.codeAliases || []).length ? (
                <span className="kw-code kw-muted">{row.codeAliases.join(", ")}</span>
              ) : (
                "—"
              ),
          },
          ...(isOrgAdmin
            ? [
                {
                  key: "kyc",
                  header: t("Bank & Aadhaar"),
                  hideSm: true,
                  cell: (row: any) =>
                    row.kyc?.accountMasked || row.kyc?.aadhaarMasked ? (
                      <span className="kw-code kw-muted">
                        {[row.kyc?.accountMasked, row.kyc?.aadhaarMasked].filter(Boolean).join(" · ")}
                      </span>
                    ) : (
                      "—"
                    ),
                },
              ]
            : []),
          { key: "active", header: "Active", cell: (row) => (row.active ? "On" : "Off") },
        ]}
      />

      {pages > 1 ? (
        <div className="kw-toolbar" style={{ marginTop: 12 }}>
          <Button size="sm" outline disabled={page === 0} onClick={() => goPage(page - 1)}>
            Previous
          </Button>
          <span className="kw-muted">
            Page {page + 1} of {pages}
          </span>
          <Button size="sm" outline disabled={page + 1 >= pages} onClick={() => goPage(page + 1)}>
            Next
          </Button>
        </div>
      ) : null}

      <Modal isOpen={open} toggle={() => setOpen(false)} modalClassName="kw-modal">
        <ModalHeader toggle={() => setOpen(false)}>{editing ? `Edit ${editing.workerCode}` : "Add worker"}</ModalHeader>
        <ModalBody>
          <Label for="kw-name">Name</Label>
          <Input
            id="kw-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
          />
          <Label className="mt-2" for="kw-cat">
            Category
          </Label>
          <Input
            id="kw-cat"
            type="select"
            value={form.categoryId}
            onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
          >
            <option value="">Choose category</option>
            {categories
              .filter((c) => c.active || c.id === form.categoryId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.prefix} · {c.name}
                </option>
              ))}
          </Input>
          <Label className="mt-2" for="kw-code">
            Worker code
          </Label>
          <Input
            id="kw-code"
            className="kw-code"
            value={form.workerCode}
            onChange={(e) => setForm({ ...form, workerCode: e.target.value.toUpperCase() })}
            placeholder={editing ? "" : generatedPreview ? `Leave blank for ${generatedPreview}` : "e.g. K_542"}
          />
          {codeChanged ? (
            <p className="kw-muted mt-1">The old code {editing.workerCode} will keep working as an old code.</p>
          ) : null}
          {editing ? (
            <div className="kw-ids">
              <span className="kw-muted">
                {t("HKP ID")}: <HkpId value={editing.hkpId} />
              </span>
              <span className="kw-muted">
                {t("Studio ID")}: <span className="kw-code">{editing.studioId || editing.workerCode}</span>{" "}
                {t("(same as the worker code)")}
              </span>
            </div>
          ) : null}
          <Label className="mt-2" for="kw-addr">
            Address (optional)
          </Label>
          <Input id="kw-addr" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <Label className="mt-2" for="kw-phone">
            Phone (optional)
          </Label>
          <Input
            id="kw-phone"
            type="tel"
            inputMode="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          {!editing && candidates.length ? (
            <>
              <Label className="mt-2" for="kw-login">
                {t("Link an existing HKP user (optional)")}
              </Label>
              <Input
                id="kw-login"
                type="select"
                value={form.userId}
                onChange={(e) => setForm({ ...form, userId: e.target.value })}
              >
                <option value="">{t("None — HKP adds one from the category")}</option>
                {candidates.map((person) => (
                  <option key={person.userId} value={person.userId}>
                    {candidateLabel(person, candidates)}
                  </option>
                ))}
              </Input>
            </>
          ) : null}
          <Label className="mt-2" for="kw-notes">
            Notes
          </Label>
          <Input
            id="kw-notes"
            type="textarea"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
          {editing && isOrgAdmin && geoRecordId(editing) ? <WorkerKycSection workerId={geoRecordId(editing)} /> : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button className="ResetBtn" disabled={saving} onClick={persist}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default KarkhanaWorkersPage;
